import React, { Component, Suspense } from "react";
import { Route, withRouter, Switch, Redirect, NavLink } from "react-router-dom";
import {
  Badge,
  Dropdown,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Progress,
} from "reactstrap";
import moment from "moment";
import axios from "axios";
import { END_POINT } from "../../constants/defaultValues";
import "./dashboard.css";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const FBDashboard = React.lazy(() => import("./FBDashboard"));
const FBCampaigns = React.lazy(() => import("./FBCampaigns"));
const FBReports = React.lazy(() => import("./FBReports"));
const FBOptimization = React.lazy(() => import("./FBOptimization"));
const FBLive = React.lazy(() => import("./FBLive"));

class FBAdsApp extends Component {
  state = {
    shopDomain: "",
    isNotificationOpen: false,
    selectedDatePreset: "30 ngày",
    startDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    accountInfo: {
      name: "Đang tải...",
      id: "...",
    },
    adAccounts: [],
    fbConnected: false,
    todaySpend: 0,
    dailyLimit: 0,
    alerts: [],
    isCustomDateOpen: false,
    isLoading: true,
  };

  componentDidMount() {
    this.fetchSidebarData();
  }

  fetchSidebarData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const resStatus = await axios.get(`${END_POINT}/api/fb-ads/auth/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { connected, ad_account_id, name, daily_spend_limit } =
        resStatus.data;
      this.setState({ fbConnected: connected });

      if (connected) {
        try {
          const resAccs = await axios.get(
            `${END_POINT}/api/fb-ads/auth/accounts`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          this.setState({ adAccounts: resAccs.data.data || [] });
        } catch (e) {
          console.error("Lỗi fetch FB accounts", e);
        }

        if (ad_account_id) {
          this.setState({
            accountInfo: { name: name || "Tài khoản QC", id: ad_account_id },
          });

          // 1. Fetch campaigns to calculate total daily budget (the real daily limit)
          const resCamps = await axios.get(
            `${END_POINT}/api/fb-ads/campaigns`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          const campaigns = resCamps.data.data || [];
          const activeCamps = campaigns.filter((c) => c.status === "ACTIVE");
          const totalDailyBudget = activeCamps.reduce(
            (sum, c) => sum + (parseInt(c.budget, 10) || 0),
            0,
          );

          // 2. Fetch today's spend
          const today = moment().format("YYYY-MM-DD");
          const resInsights = await axios.get(
            `${END_POINT}/api/fb-ads/campaigns/dashboard/insights`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { since: today, until: today },
            },
          );

          const todayData = resInsights.data.data?.[0] || { spend: 0 };

          // 3. Generate dynamic alerts for the header
          const dynamicAlerts = [];
          activeCamps.forEach((c) => {
            if (c.budget > 0 && c.spend / c.budget > 0.85) {
              dynamicAlerts.push({
                title: "Ngân sách cao",
                detail: `Camp '${c.name}' dùng >85% budget`,
                type: "warning",
              });
            }
          });
          campaigns
            .filter((c) => c.status === "PAUSED")
            .slice(0, 1)
            .forEach((c) => {
              dynamicAlerts.push({
                title: "Campaign Tắt",
                detail: `'${c.name}' đã tạm dừng`,
                type: "info",
              });
            });

          this.setState({
            todaySpend: todayData.spend || 0,
            dailyLimit: totalDailyBudget,
            alerts: dynamicAlerts,
          });
        }
      }
    } catch (err) {
      console.error("Sidebar fetch error", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleConnectFB = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${END_POINT}/api/fb-ads/auth/login`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.url) {
        let popup = window.open(res.data.url, "FBAuth", "width=600,height=700");
        let checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            this.fetchSidebarData();
            window.location.reload();
          }
        }, 1000);
      }
    } catch (err) {
      console.error(err);
    }
  };

  handleSelectAccount = async (ad_account_id) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${END_POINT}/api/fb-ads/auth/account`,
        { ad_account_id },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      this.fetchSidebarData();
      window.location.reload();
    } catch (err) {
      console.error(err);
    }
  };

  handleDisconnectFB = async () => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${END_POINT}/api/fb-ads/auth/disconnect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.reload();
    } catch (err) {
      console.error(err);
    }
  };

  toggleNotification = () => {
    this.setState((prevState) => ({
      isNotificationOpen: !prevState.isNotificationOpen,
    }));
  };

  setDatePreset = (preset) => {
    let start = moment().subtract(29, "days");
    let end = moment();

    if (preset === "Hôm nay") {
      start = moment();
    } else if (preset === "Hôm qua") {
      start = moment().subtract(1, "days");
      end = moment().subtract(1, "days");
    } else if (preset === "7 ngày") {
      start = moment().subtract(6, "days");
    } else if (preset === "30 ngày") {
      start = moment().subtract(29, "days");
    } else if (preset === "90 ngày") {
      start = moment().subtract(89, "days");
    }

    this.setState({
      selectedDatePreset: preset,
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
    });
  };

  handleCustomDateChange = (type, date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    this.setState((prevState) => ({
      [type]: formattedDate,
      selectedDatePreset: "Tùy chỉnh",
    }));
  };

  render() {
    const { match, location } = this.props;
    const basePath = match.url === "/" ? "" : match.url;

    // Auth Guard
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      const isLocal = window.location.hostname.includes("localhost");
      return (
        <div
          className="fb-auth-guard d-flex justify-content-center align-items-center"
          style={{ minHeight: "100vh", backgroundColor: "#f0f2f5" }}
        >
          <div
            className="card text-center border-0 shadow-lg"
            style={{
              padding: "3rem",
              maxWidth: "500px",
              borderRadius: "1.5rem",
            }}
          >
            <div className="mb-4 d-inline-block p-4 bg-light rounded-circle">
              <i
                className="simple-icon-lock text-primary"
                style={{ fontSize: "3rem" }}
              ></i>
            </div>
            <h3 className="font-weight-bold mb-3">
              Yêu cầu xác thực tài khoản
            </h3>
            <p className="text-muted mb-4">
              Bạn đang truy cập hệ thống quản trị tập trung. Vui lòng quay lại
              ứng dụng VMASS để định danh chính xác cửa hàng của bạn.
            </p>
            <button
              className="btn btn-primary btn-lg btn-block rounded-pill font-weight-bold"
              onClick={() => window.history.back()}
            >
              Quay lại trang chính
            </button>
          </div>
        </div>
      );
    }

    const { selectedDatePreset, accountInfo, todaySpend, dailyLimit } =
      this.state;
    const formatMoney = (val) =>
      new Intl.NumberFormat("vi-VN", {
        style: "currency",
        currency: "VND",
      }).format(val);
    const progress =
      dailyLimit > 0
        ? Math.min(Math.round((todaySpend / dailyLimit) * 100), 100)
        : 0;

    return (
      <div
        className="fb-ads-manager-layout"
        style={{
          display: "flex",
          minHeight: "100vh",
          backgroundColor: "#f4f7fa",
        }}
      >
        {/* Sidebar */}
        <div
          className="fb-sidebar bg-white shadow-sm"
          style={{
            width: "260px",
            position: "fixed",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            zIndex: 100,
          }}
        >
          <div className="p-4 border-bottom d-flex align-items-center">
            <div
              className="bg-primary text-white rounded d-flex align-items-center justify-content-center mr-2"
              style={{ width: 32, height: 32, fontWeight: "bold" }}
            >
              Ad
            </div>
            <h5
              className="mb-0 font-weight-bold text-dark"
              style={{ letterSpacing: "-0.5px" }}
            >
              Ads Manager
            </h5>
          </div>

          <div className="sidebar-nav p-3 flex-grow-1">
            <NavLink
              to={`${basePath}/dashboard`}
              className="nav-link-custom d-flex align-items-center p-2 mb-2 text-dark text-decoration-none rounded-lg"
              activeClassName="active bg-primary-light text-primary"
            >
              <i
                className="simple-icon-speedometer mr-3"
                style={{ fontSize: "18px" }}
              ></i>
              <span>Tổng quan</span>
            </NavLink>

            <NavLink
              to={`${basePath}/campaigns`}
              className="nav-link-custom d-flex align-items-center p-2 mb-2 text-dark text-decoration-none rounded-lg"
              activeClassName="active bg-primary-light text-primary"
            >
              <i
                className="simple-icon-layers mr-3"
                style={{ fontSize: "18px" }}
              ></i>
              <span>Campaigns</span>
            </NavLink>

            <NavLink
              to={`${basePath}/reports`}
              className="nav-link-custom d-flex align-items-center p-2 mb-2 text-dark text-decoration-none rounded-lg"
              activeClassName="active bg-primary-light text-primary"
            >
              <i
                className="simple-icon-chart mr-3"
                style={{ fontSize: "18px" }}
              ></i>
              <span>Báo cáo</span>
            </NavLink>

            <NavLink
              to={`${basePath}/optimization`}
              className="nav-link-custom d-flex align-items-center p-2 mb-2 text-dark text-decoration-none rounded-lg"
              activeClassName="active bg-primary-light text-primary"
            >
              <i
                className="simple-icon-magic-wand mr-3"
                style={{ fontSize: "18px" }}
              ></i>
              <span>Tối ưu</span>
            </NavLink>

            <NavLink
              to={`${basePath}/live`}
              className="nav-link-custom d-flex align-items-center p-2 mb-2 text-dark text-decoration-none rounded-lg"
              activeClassName="active bg-primary-light text-primary"
            >
              <i
                className="simple-icon-camrecorder mr-3"
                style={{ fontSize: "18px" }}
              ></i>
              <span>Facebook Live</span>
            </NavLink>
          </div>

          {/* Bottom Sidebar Card: Daily Limit */}
          <div className="p-3 mb-3">
            <div className="bg-light p-3 rounded-lg border">
              <div className="text-small text-muted mb-1 font-weight-bold uppercase">
                Hạn mức trong ngày
              </div>
              <div className="font-weight-bold text-dark mb-2">
                {formatMoney(dailyLimit)}
              </div>
              <Progress
                value={progress}
                className="mb-1"
                style={{ height: "5px" }}
              />
              <div className="d-flex justify-content-between text-tiny text-muted">
                <span>Đã dùng: {progress}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div
          className="fb-main-content"
          style={{
            marginLeft: "260px",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top Header */}
          <div
            className="fb-header sticky-top bg-white border-bottom px-4 py-3 d-flex justify-content-between align-items-center"
            style={{ zIndex: 90 }}
          >
            <UncontrolledDropdown>
              <DropdownToggle
                tag="div"
                className="cursor-pointer"
                style={{ cursor: "pointer" }}
              >
                <div className="text-tiny text-muted uppercase font-weight-bold mb-0">
                  Tài khoản
                </div>
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 font-weight-bold text-dark">
                    {accountInfo.name}
                  </h5>
                  <Badge color="light" pill className="ml-2 text-muted">
                    {accountInfo.id}
                  </Badge>
                  <i className="simple-icon-arrow-down ml-2 text-muted"></i>
                </div>
              </DropdownToggle>
              <DropdownMenu
                className="p-0 shadow-lg border-0 mt-3"
                style={{ width: 320, borderRadius: "15px" }}
              >
                <div
                  className="p-3 border-bottom font-weight-bold bg-light"
                  style={{
                    borderTopLeftRadius: "15px",
                    borderTopRightRadius: "15px",
                  }}
                >
                  Đổi tài khoản quảng cáo
                </div>
                <div
                  className="p-2"
                  style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                  {this.state.fbConnected ? (
                    this.state.adAccounts.length > 0 ? (
                      this.state.adAccounts.map((acc, idx) => (
                        <div
                          key={idx}
                          className="p-3 border-bottom hover-bg-light rounded cursor-pointer transition-all d-flex justify-content-between align-items-center"
                          onClick={() => this.handleSelectAccount(acc.id)}
                        >
                          <div>
                            <div
                              className={`text-small font-weight-bold ${acc.id === accountInfo.id ? "text-primary" : "text-dark"}`}
                            >
                              {acc.name}
                            </div>
                            <div className="text-tiny text-muted">
                              ID: {acc.id} • {acc.currency}
                            </div>
                          </div>
                          {acc.id === accountInfo.id && (
                            <i className="simple-icon-check text-primary font-weight-bold"></i>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-muted text-tiny">
                        Không có tài khoản QC nào.
                        <br />
                        Hoặc chưa cấp quyền Ads Management cho App FB.
                      </div>
                    )
                  ) : (
                    <div className="p-4 text-center text-muted text-tiny">
                      Vui lòng kết nối với Facebook để tải dữ liệu
                    </div>
                  )}
                </div>
                <div
                  className="p-3 bg-light d-flex justify-content-between"
                  style={{
                    borderBottomLeftRadius: "15px",
                    borderBottomRightRadius: "15px",
                  }}
                >
                  {!this.state.fbConnected ? (
                    <button
                      className="btn btn-primary btn-sm rounded-pill font-weight-bold w-100"
                      onClick={this.handleConnectFB}
                    >
                      <i className="simple-icon-social-facebook mr-2" /> Kết nối
                      Facebook
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill font-weight-bold"
                        onClick={this.handleConnectFB}
                      >
                        Cấp lại quyền
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm rounded-pill font-weight-bold"
                        onClick={this.handleDisconnectFB}
                      >
                        Ngắt kết nối
                      </button>
                    </>
                  )}
                </div>
              </DropdownMenu>
            </UncontrolledDropdown>

            <div className="d-flex align-items-center">
              {/* Quick Date Presets */}
              <div
                className="date-presets bg-light p-1 rounded-pill mr-4 d-none d-md-flex"
                style={{ display: "flex", gap: "8px" }}
              >
                {["Hôm nay", "Hôm qua", "7 ngày", "30 ngày", "90 ngày"].map(
                  (preset) => (
                    <button
                      key={preset}
                      onClick={() => this.setDatePreset(preset)}
                      className={`btn btn-sm rounded-pill px-3 border-0 shadow-none font-weight-bold ${selectedDatePreset === preset ? "bg-primary text-white" : "text-muted"}`}
                    >
                      {preset}
                    </button>
                  ),
                )}

                <Dropdown
                  isOpen={this.state.isCustomDateOpen}
                  toggle={() =>
                    this.setState({
                      isCustomDateOpen: !this.state.isCustomDateOpen,
                    })
                  }
                  className="d-inline-block"
                >
                  <DropdownToggle
                    className={`btn btn-sm rounded-pill px-3 border-0 shadow-none font-weight-bold ${selectedDatePreset === "Tùy chỉnh" ? "bg-primary text-white" : "text-muted"}`}
                  >
                    Tùy chỉnh
                  </DropdownToggle>
                  <DropdownMenu
                    right
                    className="p-3 shadow-lg border-0"
                    style={{ width: 280, borderRadius: "15px" }}
                  >
                    <div className="font-weight-bold mb-2">
                      Chọn khoảng ngày
                    </div>
                    <div className="mb-2">
                      <label className="text-tiny text-muted uppercase font-weight-bold">
                        Từ ngày
                      </label>
                      <DatePicker
                        selected={moment(this.state.startDate)}
                        onChange={(date) =>
                          this.handleCustomDateChange("startDate", date)
                        }
                        className="form-control form-control-sm rounded-pill"
                        dateFormat="DD/MM/YYYY"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="text-tiny text-muted uppercase font-weight-bold">
                        Đến ngày
                      </label>
                      <DatePicker
                        selected={moment(this.state.endDate)}
                        onChange={(date) =>
                          this.handleCustomDateChange("endDate", date)
                        }
                        className="form-control form-control-sm rounded-pill"
                        dateFormat="DD/MM/YYYY"
                      />
                    </div>
                    <button
                      className="btn btn-primary btn-block btn-sm rounded-pill font-weight-bold"
                      onClick={() => this.setState({ isCustomDateOpen: false })}
                    >
                      Áp dụng
                    </button>
                  </DropdownMenu>
                </Dropdown>
              </div>

              <Dropdown
                isOpen={this.state.isNotificationOpen}
                toggle={this.toggleNotification}
                className="mr-3"
              >
                <DropdownToggle
                  color="light"
                  className="rounded-circle p-2 border-0 shadow-none position-relative"
                >
                  <i
                    className="simple-icon-bell text-dark"
                    style={{ fontSize: "18px" }}
                  ></i>
                  {this.state.alerts.length > 0 && (
                    <Badge
                      pill
                      color="danger"
                      className="position-absolute"
                      style={{ top: -5, right: -5 }}
                    >
                      {this.state.alerts.length}
                    </Badge>
                  )}
                </DropdownToggle>
                <DropdownMenu
                  right
                  className="p-0 shadow-lg border-0"
                  style={{ width: 300, borderRadius: "15px" }}
                >
                  <div className="p-3 border-bottom font-weight-bold">
                    Thông báo mới
                  </div>
                  <div
                    className="p-2"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {this.state.alerts.length > 0 ? (
                      this.state.alerts.map((alert, idx) => (
                        <div
                          key={idx}
                          className="p-2 border-bottom hover-bg-light rounded"
                        >
                          <div
                            className={`text-small font-weight-bold ${alert.type === "warning" ? "text-warning" : alert.type === "danger" ? "text-danger" : ""}`}
                          >
                            {alert.title}
                          </div>
                          <div className="text-tiny text-muted">
                            {alert.detail}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-muted text-tiny">
                        Không có thông báo mới
                      </div>
                    )}
                  </div>
                </DropdownMenu>
              </Dropdown>

              <div className="user-profile d-flex align-items-center bg-light rounded-pill p-1">
                <div
                  className="bg-primary-light text-primary rounded-circle d-flex align-items-center justify-content-center mr-2 font-weight-bold"
                  style={{ width: 32, height: 32 }}
                >
                  PQ
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Content */}
          <div className="px-5 py-4">
            <Suspense fallback={<div className="loading" />}>
              <Switch>
                <Redirect
                  exact
                  from={`${match.url}`}
                  to={`${basePath}/dashboard`}
                />
                {match.url === "/" && (
                  <Redirect
                    exact
                    from={`${match.url}/`}
                    to={`${basePath}/dashboard`}
                  />
                )}
                <Route
                  path={`${basePath}/dashboard`}
                  render={(props) => (
                    <FBDashboard
                      {...props}
                      startDate={this.state.startDate}
                      endDate={this.state.endDate}
                      onConnectFB={this.handleConnectFB}
                    />
                  )}
                />
                <Route
                  path={`${basePath}/campaigns`}
                  render={(props) => (
                    <FBCampaigns
                      {...props}
                      startDate={this.state.startDate}
                      endDate={this.state.endDate}
                    />
                  )}
                />
                <Route
                  path={`${basePath}/reports`}
                  render={(props) => (
                    <FBReports
                      {...props}
                      startDate={this.state.startDate}
                      endDate={this.state.endDate}
                    />
                  )}
                />
                <Route
                  path={`${basePath}/optimization`}
                  render={(props) => (
                    <FBOptimization
                      {...props}
                      startDate={this.state.startDate}
                      endDate={this.state.endDate}
                    />
                  )}
                />
                <Route
                  path={`${basePath}/live`}
                  render={(props) => (
                    <FBLive
                      {...props}
                    />
                  )}
                />
                <Redirect to="/error" />
              </Switch>
            </Suspense>
          </div>
        </div>

        <style>{`
          .nav-link-custom:hover {
            background-color: #f0f7ff;
            color: #1877f2 !important;
            border-radius: 10px !important;
          }
          .bg-primary-light {
            background-color: #e7f3ff;
          }
          .cursor-not-allowed {
            cursor: not-allowed;
            opacity: 0.6;
          }
          .hover-bg-light:hover {
            background-color: #f8f9fa;
          }
        `}</style>
      </div>
    );
  }
}

export default withRouter(FBAdsApp);
